import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { Plus, AlertCircle, Rocket, Lightbulb, Folder, Target, Edit3, BrainCircuit, Zap, Palette, BarChart3, Flame } from 'lucide-react';
import { workspacesApi } from '../api/workspaces';
import { toast } from '../components/Toast';

const SPACE_ICON_CHOICES = ['Rocket', 'Lightbulb', 'Folder', 'Target', 'Edit3', 'BrainCircuit', 'Zap', 'Palette', 'BarChart3', 'Flame'];

export function SpacesPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['workspaces'],
    queryFn: workspacesApi.list,
  });

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('Rocket');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () => workspacesApi.create({ name: name.trim(), emoji: icon }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      toast.success('工作空间已创建');
      setOpen(false);
      setName('');
      setIcon('Rocket');
      setSubmitError(null);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : '创建失败';
      setSubmitError(msg);
      toast.error(msg, '创建失败');
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setSubmitError('请输入空间名称');
      return;
    }
    createMutation.mutate();
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      setName('');
      setIcon('Rocket');
      setSubmitError(null);
    }
  }

  return (
    <div className="mx-auto max-w-4xl px-8 py-12">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight text-[#1a1a2e]">我的工作空间</h1>
        <Dialog.Root open={open} onOpenChange={handleOpenChange}>
          <Dialog.Trigger asChild>
            <button type="button" className="btn btn-primary btn-md">
              <Plus className="h-4 w-4" /> 创建空间
            </button>
          </Dialog.Trigger>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-40 bg-bg-overlay backdrop-blur-sm animate-fade-in" />
            <Dialog.Content
              className="fixed left-1/2 top-1/2 z-50 w-[480px] max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-bg-surface p-6 shadow-xl ring-1 ring-black/5"
              aria-describedby={undefined}
            >
              <Dialog.Title className="text-lg font-semibold">创建工作空间</Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-text-secondary">
                创建后你将自动成为该空间的管理员。
              </Dialog.Description>

              <form onSubmit={handleSubmit} className="mt-5 space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium" htmlFor="ws-name">
                    空间名称 <span className="text-danger">*</span>
                  </label>
                  <input
                    id="ws-name"
                    type="text"
                    required
                    maxLength={32}
                    autoFocus
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input"
                    placeholder="如：产品研发空间"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">空间图标</label>
                  <div className="flex flex-wrap gap-2">
                    {SPACE_ICON_CHOICES.map((iconName) => {
                      const IconComp = iconName === 'Rocket' ? Rocket :
                        iconName === 'Lightbulb' ? Lightbulb :
                        iconName === 'Folder' ? Folder :
                        iconName === 'Target' ? Target :
                        iconName === 'Edit3' ? Edit3 :
                        iconName === 'BrainCircuit' ? BrainCircuit :
                        iconName === 'Zap' ? Zap :
                        iconName === 'Palette' ? Palette :
                        iconName === 'BarChart3' ? BarChart3 :
                        iconName === 'Flame' ? Flame : Folder;
                      return (
                        <button
                          key={iconName}
                          type="button"
                          onClick={() => setIcon(iconName)}
                          className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${
                            icon === iconName
                              ? 'border-accent bg-accent-50 text-accent'
                              : 'border-border text-text-secondary hover:bg-bg-subtle hover:text-text-primary'
                          }`}
                          aria-pressed={icon === iconName}
                          aria-label={`选择图标 ${iconName}`}
                        >
                          <IconComp className="h-4 w-4" />
                        </button>
                      );
                    })}
                  </div>
                </div>

                {submitError && (
                  <div className="flex items-center gap-2 rounded-md bg-[#fef2f2] px-3 py-2 text-sm text-danger">
                    <AlertCircle className="h-4 w-4" />
                    {submitError}
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="btn btn-ghost btn-md"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending}
                    className="btn btn-primary btn-md"
                  >
                    {createMutation.isPending ? '创建中...' : '创建'}
                  </button>
                </div>
              </form>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </div>

      {isLoading ? (
        <div className="mt-8 space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 animate-shimmer rounded-xl bg-bg-subtle" />
          ))}
        </div>
      ) : error ? (
        <div className="mt-8 flex flex-col items-center rounded-xl bg-bg-surface p-10 text-center ring-1 ring-black/5 shadow-sm">
          <AlertCircle className="h-8 w-8 text-danger" />
          <p className="mt-3 text-sm text-text-secondary">
            {error instanceof Error ? error.message : '加载失败'}
          </p>
          <button
            type="button"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['workspaces'] })}
            className="btn btn-secondary btn-md mt-4"
          >
            重试
          </button>
        </div>
      ) : data && data.length > 0 ? (
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {data.map((ws) => (
            <Link
              key={ws.id}
              to={`/space/${ws.id}`}
              className="card flex items-center gap-4 p-5"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-accent-50 to-accent-100 shadow-sm ring-1 ring-accent/20">
  {ws.emoji ? (
    <span className="text-lg">{ws.emoji}</span>
  ) : (
    <Folder className="h-5 w-5 text-accent" />
  )}
</div>
              <div className="flex-1">
                <div className="font-medium text-[#1a1a2e]">{ws.name}</div>
                <div className="text-xs text-text-tertiary">{ws.memberCount} 名成员</div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="mt-12 flex flex-col items-center text-center">
          <div className="h-24 w-24 rounded-2xl bg-bg-subtle ring-1 ring-black/5" aria-hidden />
          <p className="mt-4 text-text-secondary">还没有工作空间</p>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="btn btn-primary btn-md mt-4"
          >
            <Plus className="h-4 w-4" /> 创建首个工作空间
          </button>
        </div>
      )}
    </div>
  );
}
